import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Image as ImageIcon,
  Sparkles,
  Calendar,
  Star,
  Pencil,
  Play,
  Pause,
  Layers,
  Clock,
  Film,
  UploadCloud,
  Trash2,
  Search,
  Globe,
  Lock,
  Radio,
  ChevronDown,
  Check,
  Loader2,
} from "lucide-react";
import {
  useBackgroundStore,
  upcomingSchedule,
  makeLayer,
} from "./BackgroundProvider";
import {
  putBlob,
  makeIdbSrc,
  compressForUpload,
  mediaSrcToUrl,
  MAX_UPLOAD_LABEL,
  oversizeMessage,
} from "./mediaStore";
import BGMiniPreview from "./BGMiniPreview";
import BGEditDrawer from "./BGEditDrawer";
import SceneBuilder from "./SceneBuilder";
import { T } from "../../components/em/tokens";
import {
  PageHeader,
  Card,
  PrimaryButton,
  SecondaryButton,
  Chip,
  EmptyState,
} from "../../components/em/ui";
import { SettingsCard, Toggle } from "../../pages/Settings/settingsUi";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
import { hasPermission } from "../../utils/permissionUtils";
// Library tabs — by media kind. Legacy 'auto'/'image' kinds are normalised to
// 'static' on load (BackgroundProvider), so anything not a scene/video is an image.
const TYPE_TABS = [
  { key: "all", label: "All" },
  { key: "image", label: "Images" },
  { key: "video", label: "Videos" },
  { key: "scene", label: "Scenes" },
];
const typeOf = (b) =>
  b.kind === "scene" ? "scene" : b.kind === "video" ? "video" : "image";

const MAX_INLINE_BYTES = 5 * 1024 * 1024; // 5 MB — warning threshold

const scheduleChip = (schedule) => {
  const k = schedule?.kind || "library";
  if (k === "default")
    return { label: "Default", bg: "#dbeafe", color: "#1d4ed8" };
  if (k === "once")
    return { label: "One-time", bg: "#fef3c7", color: "#92400e" };
  if (k === "yearly")
    return { label: "Yearly", bg: "#fce7f3", color: "#be185d" };
  if (k === "timeOfDay")
    return { label: "Time-of-day", bg: "#dcfce7", color: "#166534" };
  return { label: "Library", bg: T.bgMuted, color: T.fg4 };
};

// Background picker for the Defaults card. A custom menu rather than a native
// <select> (whose option list renders as an unstyled box). `value` "" shows
// `emptyLabel`, and picking it calls onChange(""). Sizes, margin and
// line-height are reset because index.css pins buttons to 40px with padding.
//
// The option list is portalled to <body> with fixed positioning: the Defaults
// card clips its content (overflow: hidden), which cut an inline menu off at
// the card's bottom edge.
function BgSelect({ value, options, onChange, emptyLabel = "None" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { left, top, minWidth } in viewport px
  const ref = useRef(null);      // trigger wrapper
  const menuRef = useRef(null);  // portalled list

  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const menuH = Math.min(280, (options.length + 1) * 32 + 10);
    // Open upward when there's no room below.
    const below = r.bottom + 4 + menuH <= window.innerHeight - 8;
    setPos({
      left: Math.min(r.left, window.innerWidth - 328),
      top: below ? r.bottom + 4 : Math.max(8, r.top - 4 - menuH),
      minWidth: r.width,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    // A fixed menu would float away from its button on scroll / resize.
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);
  const all = [{ value: "", label: emptyLabel }, ...options];
  const current = all.find((o) => o.value === value) || all[0];
  const row = {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
    width: "100%", height: 32, lineHeight: "normal", margin: 0, padding: "0 10px",
    border: 0, borderRadius: T.rSm, cursor: "pointer", textAlign: "left",
  };
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          if (!open) place();
          setOpen((o) => !o);
        }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8, height: 34, maxWidth: 280,
          lineHeight: "normal", margin: 0, padding: "0 10px 0 12px", boxSizing: "border-box",
          border: `1px solid ${open ? T.orange : T.border}`, borderRadius: T.rMd,
          background: "var(--em-bg-card)", cursor: "pointer", color: T.fg1,
          font: `600 12.5px ${T.font}`,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{current.label}</span>
        <ChevronDown size={14} color={T.fg5} style={{ width: 14, height: 14, top: 0, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: "fixed", top: pos.top, left: pos.left, minWidth: pos.minWidth, width: "max-content",
            maxWidth: 320, maxHeight: 280, overflowY: "auto", zIndex: 10000, padding: 4, boxSizing: "border-box",
            background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: T.rMd,
            boxShadow: "0 12px 32px -8px rgba(10,10,10,0.25)",
          }}
        >
          {all.map((o) => {
            const on = o.value === value;
            return (
              <button
                key={o.value || "__none"}
                type="button"
                role="option"
                aria-selected={on}
                onClick={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); }}
                style={{
                  ...row,
                  background: on ? T.orange50 : "transparent",
                  color: on ? T.orange : o.value ? T.fg2 : T.fg5,
                  font: `${on ? 600 : 500} 12.5px ${T.font}`,
                }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = T.bgMuted; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                {on && <Check size={13} style={{ width: 13, height: 13, top: 0, flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// Compact icon-only action button with a custom dark-pill tooltip on hover.
function IconBtn({
  icon: Icon,
  tip,
  onClick,
  active = false,
  tone = "default",
}) {
  // Tooltip anchor (viewport coords) while hovered, else null. The tooltip is
  // portalled to <body> with fixed positioning: the library card clips its
  // content (overflow: hidden, for the rounded preview), which cut an inline
  // tooltip off at the card edge.
  const [tipPos, setTipPos] = useState(null);
  const wrapRef = useRef(null);
  const showTip = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setTipPos({ x: r.left + r.width / 2, y: r.top });
  };
  const hideTip = () => setTipPos(null);
  // A scroll or resize would leave a fixed tooltip floating in the wrong place.
  useEffect(() => {
    if (!tipPos) return undefined;
    window.addEventListener("scroll", hideTip, true);
    window.addEventListener("resize", hideTip);
    return () => {
      window.removeEventListener("scroll", hideTip, true);
      window.removeEventListener("resize", hideTip);
    };
  }, [tipPos]);

  const palette = active
    ? { bg: T.orange50, border: T.orange, color: T.orangeDeep }
    : tone === "danger"
      ? { bg: "#fff", border: "#fecaca", color: T.danger }
      : { bg: "#fff", border: T.border, color: T.fg3 };
  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onFocus={showTip}
      onBlur={hideTip}
    >
      {/* margin / line-height / min-width reset: the global `button` rule in
          index.css adds a margin and a 40px line box, which pushed the row of
          five past the card edge. */}
      <button
        type="button"
        onClick={onClick}
        aria-label={tip}
        style={{
          padding: 0,
          margin: 0,
          width: 30,
          height: 30,
          minWidth: 30,
          lineHeight: 0,
          boxSizing: "border-box",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: T.rMd,
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          color: palette.color,
          cursor: "pointer",
          transition: "background 120ms, border-color 120ms",
        }}
      >
        <Icon size={14} style={{ width: 14, height: 14, top: 0, margin: 0 }} />
      </button>
      {tipPos &&
        tip &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: "fixed",
              top: tipPos.y - 6,
              // Keep the bubble on screen near the viewport edges.
              left: Math.min(Math.max(tipPos.x, 90), window.innerWidth - 90),
              transform: "translate(-50%, -100%)",
              background: T.bgBlack,
              color: "#fff",
              font: `500 11px ${T.font}`,
              padding: "4px 8px",
              borderRadius: 6,
              whiteSpace: "nowrap",
              zIndex: 10000,
              pointerEvents: "none",
              boxShadow: "0 4px 12px -4px rgba(0,0,0,0.45)",
            }}
          >
            {tip}
          </span>,
          document.body,
        )}
    </div>
  );
}

export default function BackgroundsSettings() {
  // Uploading a still image needs no permission at all; video/GIF uploads
  // and building layered scenes need their own grant. The server enforces the
  // same split on POST /media (by mimetype) and POST /scenes.
  // `administer_backgrounds` — every background action; it satisfies each of
  // the narrower keys below. (Ownership overrides come from the server's
  // per-item canEdit / canShare, so they need no client-side flag.)
  const hasFullAccess = hasPermission("administer_backgrounds");
  const canManageScenes = hasFullAccess || hasPermission("manage_background_scenes");
  // Org-wide library settings (defaults, built-in seeds).
  const canManageLibrary = hasFullAccess || hasPermission("manage_backgrounds");
  // Edit/delete: an upload belongs to its uploader alone — the server sends
  // `canEdit` per item (managers get no override on someone else's upload).
  // Built-in seeds have no uploader; they're library settings.
  const canModify = (b) =>
    String(b?.id || "").startsWith("seed-") ? canManageLibrary : !!b?.canEdit;
  const openEditor = (b) => {
    if (canModify(b)) setEditId(b.id);
  };

  const {
    state,
    active,
    addBackground,
    setLive,
    setDefault,
    setEmployeeDefault,
    setParallaxEnabled,
    deleteBackground,
    uploads,
    startUpload,
    endUpload,
  } = useBackgroundStore();

  // Defaults dropdowns: only backgrounds everyone can see (built-ins, or
  // library items shared with everyone).
  const defaultOptions = useMemo(
    () =>
      state.backgrounds
        .filter((b) => String(b.id).startsWith("seed-") || (b.shared && !b.personal))
        .map((b) => ({ value: b.id, label: b.name || "Untitled" })),
    [state.backgrounds],
  );
  const nameOf = (id) => (id ? state.backgrounds.find((b) => b.id === id)?.name : null);

  const [typeTab, setTypeTab] = useState("all"); // TYPE_TABS key
  const [search, setSearch] = useState("");
  const [editId, setEditId] = useState(null);
  const [sceneId, setSceneId] = useState(null);
  const [sceneNew, setSceneNew] = useState(false);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const editing = useMemo(
    () => state.backgrounds.find((b) => b.id === editId) || null,
    [editId, state.backgrounds],
  );
  const sceneEditing = useMemo(
    () => state.backgrounds.find((b) => b.id === sceneId) || null,
    [sceneId, state.backgrounds],
  );

  // Name search narrows the pool; the tab picks the kind within it. Tab counts
  // come from the same pool, so each number matches what that tab would show.
  const pool = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return state.backgrounds;
    return state.backgrounds.filter((b) =>
      String(b.name || "").toLowerCase().includes(q),
    );
  }, [state.backgrounds, search]);

  const typeCounts = useMemo(() => {
    const c = { all: pool.length, image: 0, video: 0, scene: 0 };
    pool.forEach((b) => { c[typeOf(b)] += 1; });
    return c;
  }, [pool]);

  const filtered = useMemo(
    () => (typeTab === "all" ? pool : pool.filter((b) => typeOf(b) === typeTab)),
    [pool, typeTab],
  );

  const upcoming = useMemo(
    () => upcomingSchedule(state, new Date(), 6),
    [state],
  );

  // Single-image static upload: no parallax, no 3-layer fan.
  // Per-image sliders (blur, scale, rotate, offsetX, offsetY) live on the
  // single layer and are edited in BGEditDrawer.
  const onUploadImage = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) return;
    // Refuse an oversized file before the placeholder card appears.
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      toast.error(tooBig);
      return;
    }
    // The placeholder card appears now ("Preparing…" while it compresses);
    // addBackground carries it through uploading and saving.
    const uploadId = startUpload(file.name);
    // Re-encode before anything else. The picked file is what eventually gets
    // POSTed to /media, and a raw camera photo is many times the reverse
    // proxy's body limit — that mismatch is what surfaced as a 413 on the live
    // host. Compressing here fixes it for both the IndexedDB copy and the
    // upload, and keeps the two in sync.
    try {
      const blob = await compressForUpload(file);
      if (blob.size > MAX_INLINE_BYTES) {
        // Still large after compression: stash in IndexedDB for capacity.
        const key = "img-" + Math.random().toString(36).slice(2, 9);
        await putBlob(key, blob);
        const src = makeIdbSrc(key);
        addStaticBg(file.name, src, src, uploadId);
      } else {
        const src = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = () => rej(new Error("Couldn't read that image."));
          r.readAsDataURL(blob);
        });
        addStaticBg(file.name, src, src, uploadId);
      }
    } catch (e) {
      endUpload(uploadId);
      toast.error(e?.message || "Couldn't read that image.");
    }
  };

  const addStaticBg = (fileName, src, thumb, uploadId) => {
    const id = "bg-" + Math.random().toString(36).slice(2, 9);
    addBackground({
      id,
      name: String(fileName).replace(/\.[^.]+$/, ""),
      tags: ["custom"],
      type: "image",
      src,
      thumb,
      kind: "static",
      layers: [
        makeLayer(src, {
          depth: 0,
          scale: 100,
          rotate: 0,
          blur: 0,
          offsetX: 0,
          offsetY: 0,
        }),
      ],
      priority: 20,
      schedule: { kind: "library" },
      settings: {
        blur: 0,
        dim: 25,
        parallax: 0,
        overlayColor: "#0a0a0a",
        overlayAlpha: 0,
        scale: 100,
      },
    }, { uploadId });
  };

  // Video / GIF upload — no per-image settings, respects dim/overlay/schedule.
  const onUploadVideo = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) return;
    const isGif = file.type === "image/gif";
    const mediaType = isGif ? "gif" : "video";
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      toast.error(tooBig);
      return;
    }
    // No smaller "may be rejected" threshold: files over the proxy-safe size
    // go up in chunks (mediaStore.syncToBackend), so anything within the
    // 10 MB limit checked above is accepted.
    const uploadId = startUpload(file.name);
    try {
      const key =
        (isGif ? "gif-" : "vid-") + Math.random().toString(36).slice(2, 9);
      await putBlob(key, file);
      const src = makeIdbSrc(key);
      addVideoBg(file.name, src, src, mediaType, uploadId);
    } catch (e) {
      endUpload(uploadId);
      toast.error(e?.message || "Couldn't read that file.");
    }
  };

  const addVideoBg = (fileName, src, thumb, mediaType, uploadId) => {
    const id = "bg-" + Math.random().toString(36).slice(2, 9);
    addBackground({
      id,
      name: String(fileName).replace(/\.[^.]+$/, ""),
      tags: ["custom", "video"],
      type: mediaType,
      mediaType,
      src,
      thumb,
      kind: "video",
      layers: [
        makeLayer(src, {
          depth: 0,
          scale: 100,
          rotate: 0,
          blur: 0,
          offsetX: 0,
          offsetY: 0,
        }),
      ],
      priority: 20,
      schedule: { kind: "library" },
      settings: {
        blur: 0,
        dim: 20,
        parallax: 0,
        overlayColor: "#0a0a0a",
        overlayAlpha: 0,
        scale: 100,
      },
    }, { uploadId });
  };

  // Open the Scene Builder on a blank draft. The scene is only created (POSTed
  // to the server) when the user clicks Save inside the builder — so cancelling
  // leaves nothing behind, and there's no client/server id mismatch.
  const startNewScene = () => {
    setSceneId(null);
    setSceneNew(true);
  };

  // ── small reusable pieces ─────────────────────────────────────────────
  const parallaxPill = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px 6px 14px",
        borderRadius: T.pill,
        border: `1px solid ${T.border}`,
        background: "#fff",
        boxShadow: T.shadowXs,
      }}
    >
      {state.parallaxEnabled ? (
        <Play size={13} color={T.orange} />
      ) : (
        <Pause size={13} color={T.fg5} />
      )}
      <span style={{ font: `600 12px/1 ${T.font}`, color: T.fg2 }}>
        Parallax
      </span>
      <Toggle checked={state.parallaxEnabled} onChange={setParallaxEnabled} />
    </div>
  );

  return (
    <div
      style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "0 32px 48px",
        font: T.font,
      }}
    >
      <PageHeader
        icon={ImageIcon}
        title="Dashboard Backgrounds"
        subtitle="Set a default, schedule seasonal or time-of-day backgrounds, and fine-tune depth."
        actions={
          <>
            {parallaxPill}
          </>
        }
      />

      {/* Now showing + Upcoming schedule */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <Card padding={0} style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 18px",
              borderBottom: `1px solid ${T.divider}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: T.rMd,
                    background: T.orange50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Sparkles size={16} color={T.orange} />
                </div>
                <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                  Now showing
                </div>
              </div>
              <Chip bg="#ecfccb" color="#3f6212" border="1px solid #bef264">
                <Sparkles size={11} /> {active.reason}
              </Chip>
            </div>
            <div
              style={{
                font: `400 12px ${T.font}`,
                color: T.fg5,
                paddingLeft: 42,
              }}
            >
              Live preview of what{" "}
              {state.role === "admin" ? "admins" : "employees"} see right now
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <BGMiniPreview bg={active.bg} height={220} />
          </div>
        </Card>

        <Card padding={0}>
          <div
            style={{
              padding: "16px 18px",
              borderBottom: `1px solid ${T.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: T.rMd,
                background: T.orange50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Calendar size={16} color={T.orange} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                Upcoming schedule
              </div>
              <div
                style={{
                  font: `400 12px ${T.font}`,
                  color: T.fg5,
                  marginTop: 2,
                }}
              >
                Backgrounds automatically queued by date or time
              </div>
            </div>
          </div>
          <div style={{ padding: "8px 0", maxHeight: 240, overflowY: "auto" }}>
            {upcoming.length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  font: `400 12px ${T.font}`,
                  color: T.fg7,
                }}
              >
                <Clock size={20} style={{ opacity: 0.4 }} />
                <div style={{ marginTop: 6 }}>
                  No scheduled backgrounds — try setting a holiday or
                  time-of-day one.
                </div>
              </div>
            )}
            {upcoming.map((it, i) => (
              <div
                key={i}
                onClick={() => openEditor(it.bg)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 18px",
                  cursor: canModify(it.bg) ? "pointer" : "default",
                  borderBottom:
                    i === upcoming.length - 1 ? 0 : `1px solid ${T.divider}`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 36,
                    borderRadius: T.rSm,
                    background: T.bgMuted,
                    backgroundImage: it.bg.thumb
                      ? `url("${mediaSrcToUrl(it.bg.thumb)}")`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    flexShrink: 0,
                    border: `1px solid ${T.border}`,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      font: `600 12px ${T.font}`,
                      color: T.fg1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.bg.name}
                  </div>
                  <div style={{ font: `400 11px ${T.font}`, color: T.fg5 }}>
                    {it.kind === "timeOfDay"
                      ? `${it.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – ${it.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                      : `${it.start.toLocaleDateString()} – ${it.end.toLocaleDateString()}`}
                  </div>
                </div>
                <Chip
                  bg={scheduleChip(it.bg.schedule).bg}
                  color={scheduleChip(it.bg.schedule).color}
                >
                  P{it.bg.priority || 0}
                </Chip>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Defaults summary */}
      <SettingsCard
        icon={Star}
        title="Defaults"
        subtitle="What each audience sees when nothing is scheduled"
        bodyPadding="20px 24px"
        style={{ marginBottom: 20 }}
      >
        {/* Pick the defaults here (they used to be per-card icons). The
            dropdowns are for Full Background Access (`administer_backgrounds`)
            only — everyone else sees the current choice as text; the server
            enforces the same. Only backgrounds everyone can see are offered —
            the server refuses a private upload as a default. */}
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", font: `400 13px ${T.font}`, color: T.fg4 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            Admin default:
            {hasFullAccess ? (
              <BgSelect
                value={state.defaultId || ""}
                options={defaultOptions}
                onChange={(id) => setDefault(id || null)}
                emptyLabel="None"
              />
            ) : (
              <b style={{ color: T.fg1 }}>{nameOf(state.defaultId) || "—"}</b>
            )}
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            Employees see:
            {hasFullAccess ? (
              <BgSelect
                value={state.employeeDefaultId || ""}
                options={defaultOptions}
                onChange={(id) => setEmployeeDefault(id || null)}
                emptyLabel="Same as admin default"
              />
            ) : (
              <b style={{ color: T.fg1 }}>{nameOf(state.employeeDefaultId) || "Same as admin default"}</b>
            )}
          </label>
          {state.myLiveId && nameOf(state.myLiveId) && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.orange, font: `600 12.5px ${T.font}` }}>
              <Radio size={13} /> Your live background: {nameOf(state.myLiveId)} — overrides schedules and defaults, for you only
            </span>
          )}
        </div>
      </SettingsCard>

      {/* Library section */}
      <SettingsCard
        icon={Layers}
        title="Background library"
        subtitle={`Upload media, build layered scenes, and pick defaults · Max ${MAX_UPLOAD_LABEL} per file`}
        bodyPadding="0"
        actions={
          <>
            <SecondaryButton onClick={() => imageRef.current?.click()}>
              <UploadCloud size={15} /> Upload image
            </SecondaryButton>
            {canManageScenes && (
              <SecondaryButton onClick={() => videoRef.current?.click()}>
                <Film size={15} /> Upload video / GIF
              </SecondaryButton>
            )}
            {canManageScenes && (
              <PrimaryButton onClick={startNewScene}>
                <Layers size={15} /> Create scene
              </PrimaryButton>
            )}
          </>
        }
      >
        <input
          ref={imageRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            onUploadImage(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={videoRef}
          type="file"
          accept="video/mp4,video/webm,image/gif"
          style={{ display: "none" }}
          onChange={(e) => {
            onUploadVideo(e.target.files);
            e.target.value = "";
          }}
        />

        {/* Toolbar — kind tabs (left) · name search (right).
            Buttons/selects reset height, margin, line-height and padding
            explicitly: the global index.css rules pin every button to 40px
            tall with 16px side padding, and give selects a chevron background. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "16px 24px 14px",
          }}
        >
          <div
            role="tablist"
            aria-label="Background type"
            style={{
              display: "inline-flex",
              gap: 2,
              padding: 3,
              borderRadius: T.rMd,
              background: T.bgMuted,
            }}
          >
            {TYPE_TABS.map((t) => {
              const on = typeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTypeTab(t.key)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    height: 28,
                    lineHeight: "28px",
                    margin: 0,
                    padding: "0 10px",
                    border: 0,
                    borderRadius: T.rSm,
                    cursor: "pointer",
                    background: on ? "var(--em-bg-card)" : "transparent",
                    boxShadow: on ? T.shadowSm : "none",
                    color: on ? T.orange : T.fg4,
                    font: `${on ? 600 : 500} 12px ${T.font}`,
                    transition: "background .15s, color .15s",
                  }}
                >
                  {t.label}
                  <span
                    style={{
                      font: `500 11px ${T.font}`,
                      color: on ? T.orange : T.fg7,
                      opacity: on ? 0.8 : 1,
                    }}
                  >
                    {typeCounts[t.key]}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1 }} />

          {/* Search by name */}
          <div style={{ position: "relative", width: 260, maxWidth: "100%" }}>
            <Search
              size={14}
              color={T.fg7}
              style={{
                position: "absolute",
                left: 11,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search backgrounds"
              aria-label="Search backgrounds by name"
              style={{
                width: "100%",
                height: 34,
                boxSizing: "border-box",
                margin: 0,
                padding: "0 12px 0 32px",
                border: `1px solid ${T.border}`,
                borderRadius: T.rMd,
                background: "var(--em-bg-card)",
                color: T.fg1,
                font: `400 12.5px ${T.font}`,
                outline: "none",
              }}
            />
          </div>

        </div>

        {/* Cards grid */}
        <div style={{ padding: "0 24px 24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
              gap: 14,
            }}
          >
            {/* Uploads still in progress — a placeholder card each, until
                the background is saved and its real card takes over. */}
            {uploads.map((u) => (
              <UploadingCard key={u.id} upload={u} />
            ))}
            {filtered.map((b) => {
              const sch = scheduleChip(b.schedule);
              const isLive = state.myLiveId === b.id;
              const isDefault = state.defaultId === b.id;
              const isEmpDefault = state.employeeDefaultId === b.id;
              const isActive = active.bg?.id === b.id;
              return (
                <div
                  key={b.id}
                  style={{
                    borderRadius: T.rXl,
                    overflow: "hidden",
                    border: isActive
                      ? `2px solid ${T.orange}`
                      : `1px solid ${T.border}`,
                    background: "#fff",
                    boxShadow: T.shadowXs,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    onClick={() => openEditor(b)}
                    style={{
                      cursor: canModify(b) ? "pointer" : "default",
                      position: "relative",
                    }}
                  >
                    <BGMiniPreview
                      bg={b}
                      height={140}
                      interactive={false}
                      showLabel={false}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        display: "flex",
                        gap: 4,
                      }}
                    >
                      {isDefault && (
                        <Chip
                          bg={T.fg1}
                          color="#fff"
                          style={{ padding: "3px 8px" }}
                        >
                          <Star size={10} /> DEFAULT
                        </Chip>
                      )}
                      {/* LIVE = pinned with "Set as live"; NOW SHOWING = on
                          screen for another reason (a schedule or a default). */}
                      {isLive ? (
                        <Chip
                          bg={T.orange}
                          color="#fff"
                          style={{ padding: "3px 8px" }}
                        >
                          <Radio size={10} /> LIVE
                        </Chip>
                      ) : isActive && (
                        <Chip
                          bg="rgba(15,23,42,0.75)"
                          color="#fff"
                          style={{ padding: "3px 8px" }}
                        >
                          <Sparkles size={10} /> NOW SHOWING
                        </Chip>
                      )}
                    </div>
                  </div>
                  {/* Body fills the card; the action row is pinned to the
                      bottom so cards in a grid row line up whether or not
                      they carry a layer-count or Shared chip. */}
                  <div
                    style={{
                      padding: 12,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                        minHeight: 24,
                      }}
                    >
                      <div
                        style={{
                          font: `600 13px ${T.font}`,
                          color: T.fg1,
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.name}
                      </div>
                      {b.kind === "scene" && (
                        <Chip
                          bg={T.orange50}
                          color={T.orangeDeep}
                          border={`1px solid ${T.orange100}`}
                        >
                          <Layers size={10} /> {b.layers?.length || 0}
                        </Chip>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        flexWrap: "wrap",
                        marginBottom: 10,
                      }}
                    >
                      <Chip bg={sch.bg} color={sch.color}>
                        {sch.label}
                      </Chip>
                      {isEmpDefault && (
                        <Chip bg="#cffafe" color="#0e7490">
                          <Star size={10} /> Employee default
                        </Chip>
                      )}
                      {/* Who can see it — on my own uploads (and legacy rows).
                          Someone else's unshared upload never reaches this list. */}
                      {!String(b.id).startsWith("seed-") &&
                        (b.shared && !b.personal ? (
                          b.mine && (
                            <Chip bg="#dcfce7" color="#008236">
                              <Globe size={10} /> Shared
                            </Chip>
                          )
                        ) : (
                          <Chip bg={T.bgMuted} color={T.fg4}>
                            <Lock size={10} />{" "}
                            Private
                          </Chip>
                        ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        marginTop: "auto",
                      }}
                    >
                      {/* Set as live — for everyone who can see the card
                          (Admin included), and it changes ONLY their own
                          background, ahead of any schedule or default. Click
                          again to stop. The defaults are picked in the Defaults
                          card; "Share with everyone" lives in Edit background. */}
                      <IconBtn
                        icon={Radio}
                        active={isLive}
                        tip={
                          isLive
                            ? "Live for you — click to stop"
                            : "Set as live (only for you)"
                        }
                        onClick={() => setLive(isLive ? null : b.id)}
                      />
                      <div style={{ flex: 1 }} />
                      {/* Edit / delete — the uploader only. */}
                      {canModify(b) && (
                        <>
                          <IconBtn
                            icon={Pencil}
                            tip="Edit"
                            onClick={() => setEditId(b.id)}
                          />
                          <IconBtn
                            icon={Trash2}
                            tone="danger"
                            tip="Delete"
                            onClick={async () => {
                              if (await confirmDialog(`Delete "${b.name}"?`)) {
                                deleteBackground(b.id);
                              }
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && uploads.length === 0 && (
            <EmptyState
              icon={ImageIcon}
              title={
                search.trim()
                  ? `No backgrounds match "${search.trim()}"`
                  : "No backgrounds in this filter yet"
              }
              subtitle={
                search.trim()
                  ? "Try another name."
                  : "Upload an image or video, add one from a URL, or create a layered scene."
              }
              action={
                canManageScenes ? (
                  <PrimaryButton onClick={startNewScene}>
                    <Layers size={15} /> Create scene
                  </PrimaryButton>
                ) : null
              }
            />
          )}
        </div>
      </SettingsCard>

      {editing && (
        <BGEditDrawer
          bg={editing}
          onClose={() => setEditId(null)}
          onEditScene={(id) => {
            setEditId(null);
            setSceneId(id);
            setSceneNew(false);
          }}
        />
      )}
      {(sceneEditing || sceneNew) && (
        <SceneBuilder
          existing={sceneEditing}
          onClose={() => {
            setSceneId(null);
            setSceneNew(false);
          }}
        />
      )}
    </div>
  );
}

// Placeholder card for a background that's still on its way: compressing,
// uploading (real byte progress), then saving. Same footprint as a library
// card so the grid doesn't jump when the real card replaces it.
const UPLOAD_PHASE_LABEL = {
  preparing: "Preparing…",
  uploading: "Uploading",
  saving: "Saving…",
};

function UploadingCard({ upload }) {
  const pct = Math.round((Number(upload.pct) || 0) * 100);
  const label =
    upload.phase === "uploading" ? `Uploading ${pct}%` : UPLOAD_PHASE_LABEL[upload.phase] || "Uploading";
  // Before bytes start moving (preparing) the bar runs indeterminate.
  const determinate = upload.phase !== "preparing";
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`${upload.name}: ${label}`}
      style={{
        borderRadius: T.rXl,
        overflow: "hidden",
        border: `1px dashed ${T.orange}`,
        background: "var(--em-bg-card)",
        boxShadow: T.shadowXs,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          aspectRatio: "16 / 10",
          background: T.orange50,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          color: T.orange,
        }}
      >
        <Loader2 size={26} style={{ animation: "bgUploadSpin 0.9s linear infinite" }} />
        <span style={{ font: `600 13px ${T.font}` }}>{label}</span>
      </div>
      <div style={{ padding: "10px 12px 12px" }}>
        <div
          title={upload.name}
          style={{
            font: `600 13px ${T.font}`,
            color: T.fg1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: 8,
          }}
        >
          {upload.name}
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={determinate ? pct : undefined}
          style={{ height: 6, borderRadius: 9999, background: T.bgMuted, overflow: "hidden", position: "relative" }}
        >
          <div
            style={
              determinate
                ? { width: `${pct}%`, height: "100%", background: T.orange, transition: "width 0.2s ease" }
                : {
                    position: "absolute",
                    width: "35%",
                    height: "100%",
                    background: T.orange,
                    borderRadius: 9999,
                    animation: "bgUploadSlide 1.1s ease-in-out infinite",
                  }
            }
          />
        </div>
      </div>
      <style>{`
        @keyframes bgUploadSpin { to { transform: rotate(360deg); } }
        @keyframes bgUploadSlide { 0% { left: -35%; } 100% { left: 100%; } }
      `}</style>
    </div>
  );
}
